import { useState } from "react";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { Field, METHODS } from "./_shared";

export function EditMerchantModal({ merchant, onClose, onSaved }) {
  const { apiFetch } = useAuth();
  const programs = merchant.programs || {};
  const [name, setName] = useState(merchant.name || "");
  const [category, setCategory] = useState(merchant.category || "");
  const [methods, setMethods] = useState(merchant.methods || []);
  const [aliases, setAliases] = useState((merchant.aliases || []).join(", "));
  // SAS
  const [sasEnabled, setSasEnabled] = useState(!!programs.sas);
  const [sasPoints, setSasPoints] = useState(programs.sas?.points_text || "");
  const [sasDeal, setSasDeal] = useState(programs.sas?.deal_text || "");
  const [sasNivaa, setSasNivaa] = useState(!!programs.sas?.nivaapoeng_bonus);
  // Trumf
  const [trumfEnabled, setTrumfEnabled] = useState(!!programs.trumf);
  const [trumfBonus, setTrumfBonus] = useState(programs.trumf?.bonus_text || "");
  const [trumfCapped, setTrumfCapped] = useState(!!programs.trumf?.bonus_capped);
  const [trumfUrl, setTrumfUrl] = useState(programs.trumf?.affiliate_url || "");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggleMethod = (m) => {
    setMethods((arr) => arr.includes(m) ? arr.filter((x) => x !== m) : [...arr, m]);
  };

  const save = async () => {
    setError("");
    const payload = {
      name,
      category,
      methods,
      aliases: aliases.split(/[,\s]+/).map((a) => a.trim()).filter(Boolean),
      programs: {},
    };
    if (sasEnabled) {
      if (!sasPoints.trim()) { setError("SAS points text required"); return; }
      payload.programs.sas = {
        points_text: sasPoints.trim(),
        deal_text: sasDeal.trim(),
        nivaapoeng_bonus: sasNivaa,
      };
    } else if (programs.sas) {
      payload.programs.sas = null;  // explicit clear
    }
    if (trumfEnabled) {
      if (!trumfBonus.trim()) { setError("Trumf bonus text required"); return; }
      payload.programs.trumf = {
        bonus_text: trumfBonus.trim(),
        bonus_capped: trumfCapped,
        affiliate_url: trumfUrl.trim(),
      };
    } else if (programs.trumf) {
      payload.programs.trumf = null;
    }

    setSaving(true);
    try {
      const d = await apiFetch(`/api/merchants/${encodeURIComponent(merchant.host)}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      if (d.ok) { onSaved(); }
      else { setError(JSON.stringify(d)); }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-40 grid place-items-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="edit-merchant-modal"
    >
      <div className="w-full max-w-2xl bg-[var(--srs-surface)] border border-[var(--srs-line)] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--srs-line)] sticky top-0 bg-[var(--srs-surface)] z-10">
          <div className="flex items-center gap-3 min-w-0">
            <Pencil className="w-4 h-4 text-[#A78BFA] shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.25em] font-bold text-[#A78BFA]">EDIT MERCHANT</div>
              <div className="font-mono text-sm text-[var(--srs-ink)] truncate">{merchant.host}</div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            data-testid="edit-close-btn"
            className="w-8 h-8 grid place-items-center border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] hover:border-[#A78BFA] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Merchant name" testid="edit-name">
              <input value={name} onChange={(e) => setName(e.target.value)}
                data-testid="edit-name-input"
                className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#A78BFA] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] transition-colors" />
            </Field>
            <Field label="Category" testid="edit-category">
              <input value={category} onChange={(e) => setCategory(e.target.value)}
                data-testid="edit-category-input"
                className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#A78BFA] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] transition-colors" />
            </Field>
          </div>

          <Field label="Payment methods" testid="edit-methods">
            <div className="flex gap-2 flex-wrap">
              {METHODS.map((m) => {
                const active = methods.includes(m.key);
                return (
                  <button type="button" key={m.key} onClick={() => toggleMethod(m.key)}
                    data-testid={`edit-method-${m.key}`}
                    className={`px-3 py-2 text-[10px] tracking-[0.18em] font-bold uppercase border transition-all ${active ? (m.key === "klarna" ? "text-black" : (m.key === "applepay" ? "text-white" : "text-[var(--srs-ink)]")) : "text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)]"}`}
                    style={{
                      borderColor: active ? m.color : "#27272A",
                      background: active ? m.color : "transparent",
                    }}>
                    {m.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Aliases (comma-separated hosts)" testid="edit-aliases">
            <input value={aliases} onChange={(e) => setAliases(e.target.value)}
              placeholder="komplett.com, komplett.se"
              data-testid="edit-aliases-input"
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#A78BFA] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] font-mono placeholder:text-[var(--srs-ink-faint)] transition-colors" />
          </Field>

          {/* SAS bonus */}
          <div className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-4 space-y-3" data-testid="edit-sas-block">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input type="checkbox" checked={sasEnabled} onChange={(e) => setSasEnabled(e.target.checked)}
                data-testid="edit-sas-toggle"
                className="w-4 h-4 accent-[#6A9CFF]" />
              <span className="text-[10px] tracking-[0.25em] font-bold text-[#6A9CFF]">SAS EUROBONUS</span>
            </label>
            {sasEnabled && (
              <div className="grid sm:grid-cols-2 gap-3 pt-1">
                <input value={sasPoints} onChange={(e) => setSasPoints(e.target.value)}
                  placeholder="Tjen 15 poeng per 100 kr"
                  data-testid="edit-sas-points"
                  className="w-full bg-[var(--srs-bg)] border border-[var(--srs-line)] focus:border-[#6A9CFF] outline-none px-4 py-2.5 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] transition-colors" />
                <input value={sasDeal} onChange={(e) => setSasDeal(e.target.value)}
                  placeholder="Deal (optional)"
                  data-testid="edit-sas-deal"
                  className="w-full bg-[var(--srs-bg)] border border-[var(--srs-line)] focus:border-[#6A9CFF] outline-none px-4 py-2.5 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] transition-colors" />
                <label className="flex items-center gap-3 sm:col-span-2 cursor-pointer select-none">
                  <input type="checkbox" checked={sasNivaa} onChange={(e) => setSasNivaa(e.target.checked)}
                    data-testid="edit-sas-nivaa"
                    className="w-4 h-4 accent-[#6A9CFF]" />
                  <span className="text-xs text-[var(--srs-ink)]">Includes <strong className="text-[var(--srs-ink)]">Nivåpoeng</strong> bonus</span>
                </label>
              </div>
            )}
          </div>

          {/* Trumf bonus */}
          <div className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-4 space-y-3" data-testid="edit-trumf-block">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input type="checkbox" checked={trumfEnabled} onChange={(e) => setTrumfEnabled(e.target.checked)}
                data-testid="edit-trumf-toggle"
                className="w-4 h-4 accent-[#C4262E]" />
              <span className="text-[10px] tracking-[0.25em] font-bold text-[#C4262E]">TRUMF NETTHANDEL</span>
            </label>
            {trumfEnabled && (
              <div className="grid sm:grid-cols-2 gap-3 pt-1">
                <input value={trumfBonus} onChange={(e) => setTrumfBonus(e.target.value)}
                  placeholder="3%"
                  data-testid="edit-trumf-bonus"
                  className="w-full bg-[var(--srs-bg)] border border-[var(--srs-line)] focus:border-[#C4262E] outline-none px-4 py-2.5 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] transition-colors" />
                <input value={trumfUrl} onChange={(e) => setTrumfUrl(e.target.value)}
                  placeholder="Affiliate URL (optional)"
                  data-testid="edit-trumf-url"
                  className="w-full bg-[var(--srs-bg)] border border-[var(--srs-line)] focus:border-[#C4262E] outline-none px-4 py-2.5 text-sm text-[var(--srs-ink)] font-mono placeholder:text-[var(--srs-ink-faint)] transition-colors" />
                <label className="flex items-center gap-3 sm:col-span-2 cursor-pointer select-none">
                  <input type="checkbox" checked={trumfCapped} onChange={(e) => setTrumfCapped(e.target.checked)}
                    data-testid="edit-trumf-capped"
                    className="w-4 h-4 accent-[#C4262E]" />
                  <span className="text-xs text-[var(--srs-ink)]">Bonus is <strong className="text-[var(--srs-ink)]">capped</strong> (display as "Opptil …")</span>
                </label>
              </div>
            )}
          </div>

          {error && <div className="text-xs text-[#EF4444]" data-testid="edit-error">✗ {error}</div>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-[var(--srs-line)] sticky bottom-0 bg-[var(--srs-surface)]">
          <button onClick={onClose} disabled={saving}
            data-testid="edit-cancel-btn"
            className="px-4 py-2 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] transition-colors">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            data-testid="edit-save-btn"
            className="inline-flex items-center gap-2 px-5 py-2 text-[10px] tracking-[0.2em] font-bold uppercase bg-[#A78BFA] hover:bg-[#9170f0] disabled:bg-[var(--srs-line-soft)] disabled:text-[var(--srs-ink-faint)] text-black transition-colors">
            {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : <><Check className="w-3 h-3" /> Save changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}
